// Channel derivation — README.md "Channels (derived, never stored)".

import type { EditorDoc, SpeakerEntry } from '@roomtreatment/core';
import type { Product } from '@roomtreatment/av-catalog';
import type { Channel, ChannelGroup } from './types';
import { azimuthDeg, angularDiffDeg, isInFront } from './geometry';

/** Maps a derived Channel to the receiver terminal / pre-out group it uses.
 *  LFE has no group (subwoofers use sub-outs, not a channel group). */
export function channelGroup(channel: Channel): ChannelGroup | null {
  switch (channel) {
    case 'FL': case 'FR': return 'front';
    case 'C': return 'center';
    case 'SL': case 'SR': return 'surround';
    case 'SBL': case 'SBR': return 'surround_back';
    case 'TFL': case 'TFR': case 'TML': case 'TMR': return 'height1';
    case 'TRL': case 'TRR': return 'height2';
    case 'LFE': return null;
  }
}

function resolveProduct(
  productId: string | undefined,
  catalog: ReadonlyMap<string, Product>,
): Product | undefined {
  return productId ? catalog.get(productId) : undefined;
}

export function isSubwoofer(speaker: SpeakerEntry, product: Product | undefined): boolean {
  return speaker.subkind === 'subwoofer' || product?.type === 'subwoofer';
}

export function isOverhead(speaker: SpeakerEntry, product: Product | undefined): boolean {
  return (
    speaker.subkind === 'overhead'
    || product?.type === 'speaker_height'
    || product?.type === 'speaker_in_ceiling'
  );
}

type BedSlotName = 'FL' | 'FR' | 'C' | 'SL' | 'SR' | 'SBL' | 'SBR';

/** The bed-slot table for a layout family — README "Slots" / "Slot target
 *  azimuths". `layout` families share a bed across their `.2`/`.4` height
 *  variants (e.g. `5.1`, `5.1.2`, `5.1.4` all use the 5.1 bed). */
function bedSlotsFor(layout: EditorDoc['speakerSetup']['layout']): { name: BedSlotName; az: number }[] {
  if (layout === 'mono') return [{ name: 'C', az: 0 }];
  if (layout === 'stereo' || layout === 'stereo.2' || layout === 'stereo.4') {
    return [{ name: 'FL', az: -30 }, { name: 'FR', az: 30 }];
  }
  if (layout === '5.1' || layout === '5.1.2' || layout === '5.1.4') {
    return [
      { name: 'FL', az: -30 }, { name: 'FR', az: 30 }, { name: 'C', az: 0 },
      { name: 'SL', az: -110 }, { name: 'SR', az: 110 },
    ];
  }
  // 7.1 family
  return [
    { name: 'FL', az: -30 }, { name: 'FR', az: 30 }, { name: 'C', az: 0 },
    { name: 'SL', az: -90 }, { name: 'SR', az: 90 },
    { name: 'SBL', az: -135 }, { name: 'SBR', az: 135 },
  ];
}

/** Minimum-cost bijective match between two lists (sizes may differ — the
 *  smaller side is fully matched, the larger side's extras are left out),
 *  by exhaustive search. At most 7 elements on either side per the README
 *  ("found by exhaustive search (at most 7 speakers)"), so a pruned
 *  brute-force permutation search is exact and fast (worst case 7! = 5040
 *  branches, pruned further whenever a partial cost already exceeds the
 *  best complete cost found so far). */
function minCostMatch(cost: (i: number, j: number) => number, nA: number, nB: number): (number | null)[] {
  // `small` is matched fully; `large` supplies (possibly unused) partners.
  const swapped = nA > nB;
  const nSmall = swapped ? nB : nA;
  const nLarge = swapped ? nA : nB;
  const c = (i: number, j: number) => (swapped ? cost(j, i) : cost(i, j));

  const usedLarge = new Array<boolean>(nLarge).fill(false);
  let bestCost = Infinity;
  let bestAssign: number[] = [];
  const cur: number[] = [];

  function rec(i: number, costSoFar: number): void {
    if (costSoFar >= bestCost) return;
    if (i === nSmall) {
      bestCost = costSoFar;
      bestAssign = cur.slice();
      return;
    }
    for (let j = 0; j < nLarge; j++) {
      if (usedLarge[j]) continue;
      usedLarge[j] = true;
      cur.push(j);
      rec(i + 1, costSoFar + c(i, j));
      cur.pop();
      usedLarge[j] = false;
    }
  }
  rec(0, 0);

  // Build a result indexed by A: result[a] = matched B index, or null.
  const result: (number | null)[] = new Array(nA).fill(null);
  for (let s = 0; s < bestAssign.length; s++) {
    const largeIdx = bestAssign[s];
    if (swapped) {
      // small = B (index s -> largeIdx over A); result is indexed by A.
      result[largeIdx] = s;
    } else {
      // small = A (index s -> largeIdx over B).
      result[s] = largeIdx;
    }
  }
  return result;
}

export interface ChannelDerivation {
  channels: Record<string, Channel>;
  /** Every LFE speaker id. */
  subIds: string[];
  /** Every overhead speaker id that received a channel. */
  overheadIds: string[];
  /** Every head-level speaker id that received a bed channel. */
  bedIds: string[];
}

/** Derive every speaker's channel — README "Channels (derived, never
 *  stored)". Pure function of the document + catalog; nothing is cached on
 *  the document. */
export function deriveChannels(
  doc: EditorDoc,
  catalog: ReadonlyMap<string, Product>,
): ChannelDerivation {
  const channels: Record<string, Channel> = {};
  const subIds: string[] = [];
  const overheadIds: string[] = [];
  const bedIds: string[] = [];

  const subwoofers: SpeakerEntry[] = [];
  const overheads: SpeakerEntry[] = [];
  const headLevel: SpeakerEntry[] = [];

  for (const s of doc.speakers) {
    const product = resolveProduct(s.productId, catalog);
    if (isSubwoofer(s, product)) subwoofers.push(s);
    else if (isOverhead(s, product)) overheads.push(s);
    else headLevel.push(s);
  }

  for (const s of subwoofers) {
    channels[s.id] = 'LFE';
    subIds.push(s.id);
  }

  const listener = doc.listener;
  if (!listener) {
    // "With no listener, every geometric rule returns not-applicable and no
    // channels are derived except LFE" — README, "Listener".
    return { channels, subIds, overheadIds, bedIds };
  }
  const [lx, ly, lz] = listener.position;

  // ── Head-level: bed-slot matching by minimum total azimuth error ──────
  const slots = bedSlotsFor(doc.speakerSetup.layout);
  const headAz = headLevel.map((s) => azimuthDeg(doc, s.position[0] - lx, s.position[2] - lz));
  const assign = minCostMatch(
    (i, j) => angularDiffDeg(headAz[i], slots[j].az),
    headLevel.length,
    slots.length,
  );
  for (let i = 0; i < headLevel.length; i++) {
    const slotIdx = assign[i];
    if (slotIdx == null) continue; // surplus — no channel
    const s = headLevel[i];
    channels[s.id] = slots[slotIdx].name;
    bedIds.push(s.id);
  }

  // ── Overheads: TF*/TR* by front/back + azimuth sign, or TM* for a *.2
  //    layout with exactly 2 overheads — README "Channels" §2. ──────────
  const layout = doc.speakerSetup.layout;
  const isDotTwoLayout = layout === 'stereo.2' || layout === '5.1.2' || layout === '7.1.2';
  const useMiddle = isDotTwoLayout && overheads.length === 2;
  for (const s of overheads) {
    const dx = s.position[0] - lx;
    const dz = s.position[2] - lz;
    const az = azimuthDeg(doc, dx, dz);
    const right = az >= 0;
    let channel: Channel;
    if (useMiddle) {
      channel = right ? 'TMR' : 'TML';
    } else {
      const front = isInFront(doc, dx, dz);
      channel = front ? (right ? 'TFR' : 'TFL') : (right ? 'TRR' : 'TRL');
    }
    channels[s.id] = channel;
    overheadIds.push(s.id);
  }

  return { channels, subIds, overheadIds, bedIds };
}
